// Step 2: Add/remove line items (variant search, qty, unit cost, serial entry).
import { useState, useEffect, forwardRef, useImperativeHandle, Fragment } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Trash2, ChevronRight } from "lucide-react";
import { apiClient } from "@/lib/api/client";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Table, THead, TBody, Tr, Th, Td } from "@/components/ui/table";
import { MoneyInput } from "@/components/ui/money-input";
import { SerialXLSXUpload } from "./serial-xlsx-upload";

function fmtVND(n: number) {
  if (!isFinite(n)) return "0 ₫";
  return new Intl.NumberFormat("vi-VN").format(n) + " ₫";
}

// Editable per-row unit-cost cell (grouped thousands). Saves on blur / Enter.
// Exported: màn xem phiếu ĐÃ NHẬP (import-form-page) cũng dùng để sửa giá.
export function EditableCostCell({
  initial,
  onSave,
}: {
  initial: string;
  onSave: (raw: string) => void;
}) {
  const [raw, setRaw] = useState(String(Math.round(parseFloat(initial) || 0)));
  useEffect(() => {
    setRaw(String(Math.round(parseFloat(initial) || 0)));
  }, [initial]);
  return (
    <MoneyInput
      value={raw}
      onChange={setRaw}
      onBlur={() => onSave(raw)}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.currentTarget as HTMLInputElement).blur();
      }}
      className="h-8 w-32"
    />
  );
}

interface VariantResult {
  id: string;
  sku: string;
  productName: string;
  costPrice: string;
  serialTracked: boolean;
}

interface ImportItem {
  id: string;
  variantId: string;
  variantSku: string | null;
  productName: string | null;
  quantity: number;
  unitCost: string;
  discount: string;
  note: string;
  serials: string[];
}

// Thành tiền dòng = SL × đơn giá − chiết khấu (CK là tiền cho cả dòng).
function lineTotal(it: { quantity: number; unitCost: string; discount?: string }) {
  return (
    (parseFloat(it.unitCost) || 0) * it.quantity - (parseFloat(it.discount ?? "0") || 0)
  );
}

// Ô ghi chú dòng sửa tại chỗ — lưu khi blur / Enter (giống EditableCostCell).
function EditableNoteCell({
  initial,
  onSave,
}: {
  initial: string;
  onSave: (raw: string) => void;
}) {
  const [raw, setRaw] = useState(initial ?? "");
  useEffect(() => {
    setRaw(initial ?? "");
  }, [initial]);
  return (
    <Input
      value={raw}
      onChange={(e) => setRaw(e.target.value)}
      onBlur={() => onSave(raw)}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.currentTarget as HTMLInputElement).blur();
      }}
      placeholder="—"
      className="h-8 w-28 text-xs"
    />
  );
}

interface ItemsResponse {
  data: { import: unknown; items: ImportItem[] };
}

interface Props {
  importId: string;
  // Reports whether the "Thêm mặt hàng" form has un-added content, so the parent
  // can reflect it in the UI (and auto-add before preview/commit).
  onPendingChange?: (hasPending: boolean) => void;
}

// Handle exposed to the parent so "Xem trước / Xác nhận" can auto-add the line
// still sitting in the form — staff never have to remember "Thêm vào phiếu".
export interface ImportStepTwoHandle {
  flushPending: () => Promise<void>;
}

export const ImportFormStepTwo = forwardRef<ImportStepTwoHandle, Props>(
  function ImportFormStepTwo({ importId, onPendingChange }, ref) {
  const qc = useQueryClient();

  // Items already saved on the import
  const { data: detail } = useQuery({
    queryKey: ["import-detail", importId],
    queryFn: () =>
      apiClient
        .get<ItemsResponse>(`/v1/imports/${importId}`)
        .then((r) => r.data.data),
  });
  const items: ImportItem[] = (detail?.items as ImportItem[]) ?? [];

  // New-row state
  const [variantSearch, setVariantSearch] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [selectedVariant, setSelectedVariant] = useState<VariantResult | null>(null);
  const [qty, setQty] = useState("1");
  const [unitCost, setUnitCost] = useState("");
  const [discount, setDiscount] = useState("");
  const [lineNote, setLineNote] = useState("");
  const [serialText, setSerialText] = useState("");
  const [expandedItem, setExpandedItem] = useState<string | null>(null);

  // One IMEI per line (also splits on comma/semicolon), trimmed + deduped. When
  // any IMEI is entered, the line quantity follows the IMEI count.
  const rawSerials = Array.from(
    new Set(serialText.split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean)),
  );
  // Sản phẩm quản lý theo SKU (serialTracked=false) thì KHÔNG dùng IMEI —
  // ẩn ô IMEI, cho gõ số lượng thẳng. Khi tạo SP mới (chưa chọn variant) vẫn
  // cho nhập IMEI như cũ.
  const serialManaged = !selectedVariant || selectedVariant.serialTracked;
  const serials = serialManaged ? rawSerials : [];
  const effectiveQty = serials.length > 0 ? serials.length : parseInt(qty, 10) || 1;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(variantSearch), 300);
    return () => clearTimeout(t);
  }, [variantSearch]);

  // Tell the parent when the add form holds un-added content (a product picked/typed
  // or IMEIs entered but "Thêm vào phiếu" not yet clicked).
  const hasPending = !!(selectedVariant || variantSearch.trim() || serialText.trim());
  useEffect(() => {
    onPendingChange?.(hasPending);
    return () => onPendingChange?.(false);
  }, [hasPending, onPendingChange]);

  const { data: variantResults = [] } = useQuery({
    queryKey: ["variant-search", debouncedQ],
    queryFn: () =>
      apiClient
        .get<{ data: VariantResult[] }>("/v1/variants/search", { params: { q: debouncedQ } })
        .then((r) => r.data.data),
    enabled: debouncedQ.length >= 2,
  });

  const addMutation = useMutation({
    mutationFn: (body: object) =>
      apiClient.post(`/v1/imports/${importId}/items`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["import-detail", importId] }),
  });

  function resetForm() {
    setSelectedVariant(null);
    setVariantSearch("");
    setQty("1");
    setUnitCost("");
    setDiscount("");
    setLineNote("");
    setSerialText("");
  }

  const deleteMutation = useMutation({
    mutationFn: (itemId: string) =>
      apiClient.delete(`/v1/imports/${importId}/items/${itemId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["import-detail", importId] }),
  });

  // Edit one field of an already-added item (unitCost / discount / note) —
  // gửi đúng ô vừa sửa, BE giữ nguyên các trường vắng mặt.
  const updateItemMutation = useMutation({
    mutationFn: ({ itemId, body }: { itemId: string; body: object }) =>
      apiClient.patch(`/v1/imports/${importId}/items/${itemId}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["import-detail", importId] }),
  });

  function saveCost(itemId: string, current: string, next: string) {
    const v = next.trim();
    if (v === "" || parseFloat(v) === parseFloat(current)) return;
    updateItemMutation.mutate({ itemId, body: { unitCost: v } });
  }

  function saveDiscount(itemId: string, current: string, next: string) {
    const v = next.trim();
    if (v === "" || parseFloat(v) === parseFloat(current)) return;
    updateItemMutation.mutate({ itemId, body: { discount: v } });
  }

  function saveNote(itemId: string, current: string, next: string) {
    if (next.trim() === (current ?? "").trim()) return;
    updateItemMutation.mutate({ itemId, body: { note: next.trim() } });
  }

  // Builds the add-item body from the current form, or null when empty.
  function pendingBody() {
    const typedName = variantSearch.trim();
    if (!selectedVariant && !typedName) return null;
    return {
      ...(selectedVariant ? { variantId: selectedVariant.id } : { productName: typedName }),
      quantity: effectiveQty,
      unitCost: unitCost || "0",
      discount: discount || "0",
      note: lineNote.trim(),
      serials,
    };
  }

  // Adds the current form line to the phiếu (button + parent's flushPending).
  async function doAdd() {
    const body = pendingBody();
    if (!body) return;
    await addMutation.mutateAsync(body);
    resetForm();
  }

  function handleAdd() {
    void doAdd();
  }

  // When the user tabs/clicks back to the product box to start a NEW product,
  // auto-add the line they just finished (product + IMEIs) so they don't have to
  // press any button. Clears the form immediately so typing the next product is
  // instant; the add runs in the background.
  function autoAddOnFocus() {
    if (addMutation.isPending) return;
    if (serials.length === 0) return; // only auto-add a completed serial line
    const body = pendingBody();
    if (!body) return;
    resetForm();
    addMutation.mutate(body);
  }

  // Parent calls this on "Xem trước / Xác nhận" so a line left in the form is
  // still added — staff don't have to click "Thêm vào phiếu" for the last item.
  useImperativeHandle(ref, () => ({ flushPending: doAdd }));

  return (
    <div className="space-y-6">
      {/* Existing items */}
      {items.length > 0 && (
        <Table>
          <THead>
            <Tr>
              <Th>SKU</Th>
              <Th>Sản phẩm</Th>
              <Th className="text-right">SL</Th>
              <Th className="text-right">Đơn giá nhập</Th>
              <Th className="text-right">Chiết khấu</Th>
              <Th className="text-right">Thành tiền</Th>
              <Th>Ghi chú</Th>
              <Th className="text-right">Serials</Th>
              <Th />
            </Tr>
          </THead>
          <TBody>
            {items.map((item) => {
              const serialCount = item.serials?.length ?? 0;
              const open = expandedItem === item.id;
              return (
                <Fragment key={item.id}>
                  <Tr>
                    <Td className="font-mono text-xs">{item.variantSku ?? "—"}</Td>
                    <Td>{item.productName ?? "—"}</Td>
                    <Td className="text-right">{item.quantity}</Td>
                    <Td className="text-right">
                      <EditableCostCell
                        initial={item.unitCost}
                        onSave={(raw) => saveCost(item.id, item.unitCost, raw)}
                      />
                    </Td>
                    <Td className="text-right">
                      <EditableCostCell
                        initial={item.discount ?? "0"}
                        onSave={(raw) => saveDiscount(item.id, item.discount ?? "0", raw)}
                      />
                    </Td>
                    <Td className="text-right font-medium">{fmtVND(lineTotal(item))}</Td>
                    <Td>
                      <EditableNoteCell
                        initial={item.note ?? ""}
                        onSave={(raw) => saveNote(item.id, item.note ?? "", raw)}
                      />
                    </Td>
                    <Td className="text-right">
                      {serialCount > 0 ? (
                        <button
                          type="button"
                          onClick={() => setExpandedItem(open ? null : item.id)}
                          className="inline-flex items-center gap-1 text-primary hover:underline"
                        >
                          <ChevronRight size={13} className={`transition-transform ${open ? "rotate-90" : ""}`} />
                          {serialCount} IMEI
                        </button>
                      ) : (
                        <span className="text-muted-foreground">0</span>
                      )}
                    </Td>
                    <Td>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive"
                        onClick={() => deleteMutation.mutate(item.id)}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </Td>
                  </Tr>
                  {open && serialCount > 0 && (
                    <Tr>
                      <Td colSpan={9} className="bg-muted/20">
                        <div className="flex flex-wrap gap-1.5 py-1">
                          {item.serials.map((sn) => (
                            <span key={sn} className="rounded bg-background border px-2 py-0.5 font-mono text-xs">
                              {sn}
                            </span>
                          ))}
                        </div>
                      </Td>
                    </Tr>
                  )}
                </Fragment>
              );
            })}
            <Tr className="border-t-2 font-semibold">
              <Td colSpan={5} className="text-right">Tổng cộng</Td>
              <Td className="text-right text-primary">
                {fmtVND(items.reduce((sum, it) => sum + lineTotal(it), 0))}
              </Td>
              <Td colSpan={3} />
            </Tr>
          </TBody>
        </Table>
      )}

      {/* Add new row */}
      <div className="rounded-md border p-4 space-y-3 bg-muted/30">
        <p className="text-sm font-medium">Thêm mặt hàng</p>

        <div className="space-y-1.5">
          <Label>Sản phẩm — gõ tên mới hoặc chọn có sẵn</Label>
          <Input
            placeholder="Gõ tên sản phẩm hoặc SKU…"
            value={selectedVariant ? `${selectedVariant.sku} — ${selectedVariant.productName}` : variantSearch}
            onFocus={autoAddOnFocus}
            onChange={(e) => {
              setSelectedVariant(null);
              setVariantSearch(e.target.value);
            }}
          />
          {!selectedVariant && variantResults.length > 0 && (
            <ul className="rounded-md border bg-background shadow-sm divide-y text-sm max-h-40 overflow-auto">
              {variantResults.map((v) => (
                <li
                  key={v.id}
                  className="px-3 py-2 cursor-pointer hover:bg-accent"
                  onClick={() => {
                    setSelectedVariant(v);
                    setUnitCost(v.costPrice);
                  }}
                >
                  <span className="font-mono mr-2">{v.sku}</span>
                  {v.productName}
                </li>
              ))}
            </ul>
          )}
          {!selectedVariant && variantSearch.trim() !== "" && variantResults.length === 0 && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              Không có SP trùng — sẽ tạo mới:{" "}
              <span className="font-medium">{variantSearch.trim()}</span>
            </p>
          )}
        </div>

        {/* IMEI / Serial — chỉ hiện cho sản phẩm quản lý theo serial. SP quản lý
            theo SKU (giá treo, phụ kiện…) thì bỏ qua, chỉ gõ số lượng. */}
        {serialManaged ? (
          <div className="space-y-1.5">
            <Label>IMEI / Serial (mỗi dòng một mã)</Label>
            <textarea
              value={serialText}
              onChange={(e) => setSerialText(e.target.value)}
              rows={5}
              placeholder={"Mỗi IMEI một dòng, ví dụ:\n351234567890123\n351234567890124"}
              className="w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-sm focus:outline-none focus:ring-1 focus:ring-ring"
            />
            {serials.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {serials.length} IMEI → số lượng = <span className="font-medium">{serials.length}</span>
              </p>
            )}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            Sản phẩm quản lý theo SKU (không IMEI) — chỉ cần nhập số lượng.
          </p>
        )}

        <div className="flex flex-wrap gap-3">
          <div className="space-y-1 flex-1 min-w-[110px]">
            <Label>Số lượng {serials.length > 0 && "(theo IMEI)"}</Label>
            <Input
              type="number"
              min="1"
              value={serials.length > 0 ? String(serials.length) : qty}
              onChange={(e) => setQty(e.target.value)}
              disabled={serials.length > 0}
            />
          </div>
          <div className="space-y-1 flex-1 min-w-[130px]">
            <Label>Đơn giá nhập</Label>
            <MoneyInput placeholder="0" value={unitCost} onChange={setUnitCost} />
          </div>
          <div className="space-y-1 flex-1 min-w-[130px]">
            <Label>Chiết khấu (cả dòng)</Label>
            <MoneyInput placeholder="0" value={discount} onChange={setDiscount} />
          </div>
          <div className="space-y-1 flex-1 min-w-[150px]">
            <Label>Ghi chú dòng</Label>
            <Input
              placeholder="Tùy chọn"
              value={lineNote}
              onChange={(e) => setLineNote(e.target.value)}
            />
          </div>
        </div>

        <Button
          onClick={handleAdd}
          disabled={(!selectedVariant && !variantSearch.trim()) || addMutation.isPending}
          size="sm"
        >
          {addMutation.isPending ? "Đang thêm…" : "＋ Thêm mặt hàng này & nhập tiếp"}
        </Button>
      </div>

      {/* Bulk upload — file may cover one or many variants (BE detects by header). */}
      <SerialXLSXUpload
        importId={importId}
        variant={
          selectedVariant
            ? { id: selectedVariant.id, sku: selectedVariant.sku, productName: selectedVariant.productName }
            : null
        }
        unitCost={unitCost}
      />
    </div>
  );
  },
);
